import { ClipboardCheck, Play } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { StockOpnameType } from "@artisancode/api-types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: StockOpnameType;
  sequencePreview?: string;
  notes: string;
  onNotesChange: (notes: string) => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

export function StockOpnameNewSessionDialog({
  open,
  onOpenChange,
  type,
  sequencePreview,
  notes,
  onNotesChange,
  onConfirm,
  isSubmitting = false,
}: Props) {
  const isRawMaterial = type === "raw_material";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            <span>
              Mulai Sesi Opname {isRawMaterial ? "Bahan Baku" : "Produk"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isRawMaterial
              ? "Sesi ini akan menghitung fisik bahan baku (stok utuh dan stok kemasan terbuka di bar)."
              : "Sesi ini akan menghitung fisik produk jadi siap jual."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {sequencePreview && (
            <div className="rounded-md border bg-muted/40 p-3 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Perkiraan Nomor Dokumen:
              </span>
              <Badge variant="outline" className="font-mono font-semibold">
                {sequencePreview}
              </Badge>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="new-notes" className="text-xs">
              Keterangan / Catatan Sesi (Opsional):
            </Label>
            <Input
              id="new-notes"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Contoh: Opname akhir bulan, cek waste kopi"
              className="text-xs"
            />
          </div>

          <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground space-y-1.5">
            <p className="font-semibold text-foreground">Alur Pelaksanaan:</p>
            <p>
              1. Sistem akan mengambil snapshot saldo sistem saat ini untuk
              semua barang aktif.
            </p>
            <p>
              2. Sesi dimulai dalam status{" "}
              <span className="font-medium text-foreground">Draft</span> — tim
              bisa mengisi hitungan bertahap.
            </p>
            <p>
              3. Selisih stok hanya akan diposting ke kartu stok saat sesi{" "}
              <span className="font-medium text-foreground">Diselesaikan</span>.
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="min-h-[40px] w-full sm:w-auto text-xs"
          >
            Batal
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="min-h-[40px] w-full sm:w-auto text-xs gap-1.5"
          >
            <Play className="h-3.5 w-3.5 fill-current" />
            {isSubmitting ? "Membuat..." : "Mulai Hitung Sekarang"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
